/**
 * Interval data structures for representing mathematical intervals.
 * Used by calculus analysis functions (monotonicity, concavity, sign regions).
 */

/** Sign of a function value */
export type Sign = 'positive' | 'negative' | 'zero';

/**
 * Represents a mathematical interval [start, end] or (start, end).
 */
export interface Interval {
  /** Left bound of the interval */
  start: number;
  /** Right bound of the interval */
  end: number;
  /** Whether the start point is included (closed) */
  startInclusive: boolean;
  /** Whether the end point is included (closed) */
  endInclusive: boolean;
}

/**
 * An interval with an associated sign indicating whether
 * the function is positive, negative, or zero on this interval.
 */
export interface SignedInterval extends Interval {
  sign: Sign;
}

/**
 * Create an interval with sensible defaults.
 * By default, creates an open interval (start, end).
 */
export function interval(
  start: number,
  end: number,
  options?: { startInclusive?: boolean; endInclusive?: boolean },
): Interval {
  return {
    start,
    end,
    startInclusive: options?.startInclusive ?? false,
    endInclusive: options?.endInclusive ?? false,
  };
}

/**
 * Format an interval for display.
 *
 * @example
 * formatInterval({ start: -Infinity, end: 2, startInclusive: false, endInclusive: false })
 * // Returns: "(-∞, 2)"
 *
 * formatInterval({ start: 0, end: 5, startInclusive: true, endInclusive: true })
 * // Returns: "[0, 5]"
 */
export function formatInterval(i: Interval): string {
  const leftBracket = i.startInclusive ? '[' : '(';
  const rightBracket = i.endInclusive ? ']' : ')';
  const startStr = i.start === -Infinity ? '-∞' : i.start.toString();
  const endStr = i.end === Infinity ? '+∞' : i.end.toString();
  return `${leftBracket}${startStr}, ${endStr}${rightBracket}`;
}

/**
 * Format multiple intervals with union notation.
 *
 * @example
 * formatIntervals([
 *   { start: -Infinity, end: 2, startInclusive: false, endInclusive: false },
 *   { start: 5, end: Infinity, startInclusive: false, endInclusive: false }
 * ])
 * // Returns: "(-∞, 2) ∪ (5, +∞)"
 */
export function formatIntervals(intervals: Interval[]): string {
  if (intervals.length === 0) {
    return '∅';
  }
  return intervals.map(formatInterval).join(' ∪ ');
}
